import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProgressStatus } from '../types';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';

const BASE_KEY = 'yoga-me.progress.v1';

type ProgressMap = Record<string, ProgressStatus>;

export interface ProgressApi {
  map: ProgressMap;
  statusOf: (id: string) => ProgressStatus;
  setStatus: (id: string, status: ProgressStatus) => void;
  /** new -> practiced -> learned -> new */
  cycle: (id: string) => void;
}

const ORDER: ProgressStatus[] = ['new', 'practiced', 'learned'];
const RANK: Record<ProgressStatus, number> = { new: 0, practiced: 1, learned: 2 };

const keyFor = (sub: string | null) => (sub ? `${BASE_KEY}.${sub}` : BASE_KEY);

function loadFrom(key: string): ProgressMap {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}') as ProgressMap;
  } catch {
    return {};
  }
}
const saveTo = (key: string, map: ProgressMap) =>
  localStorage.setItem(key, JSON.stringify(map));

/** Union of two progress maps, keeping the higher status per pose. */
function merge(a: ProgressMap, b: ProgressMap): ProgressMap {
  const out: ProgressMap = { ...a };
  for (const [id, status] of Object.entries(b)) {
    if (!out[id] || RANK[status] > RANK[out[id]]) out[id] = status;
  }
  return out;
}

export function useProgress(): ProgressApi {
  const { status: authStatus, user } = useAuth();
  const sub = authStatus === 'authenticated' && user ? user.sub : null;
  const [map, setMap] = useState<ProgressMap>(() => loadFrom(keyFor(null)));
  const subRef = useRef<string | null>(null);

  // On identity change: anonymous → local; signed in → merge local into cloud.
  useEffect(() => {
    subRef.current = sub;
    if (!sub) {
      setMap(loadFrom(keyFor(null)));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cloud = await api.getProgress();
        const anon = loadFrom(keyFor(null));
        const merged = merge(cloud, anon);
        // Only push back if the merge actually added something local.
        if (JSON.stringify(merged) !== JSON.stringify(cloud)) {
          await api.putProgress(merged);
        }
        if (Object.keys(anon).length) localStorage.removeItem(keyFor(null));
        if (cancelled) return;
        setMap(merged);
        saveTo(keyFor(sub), merged);
      } catch (err) {
        console.error('[progress] cloud sync failed; using local cache', err);
        if (!cancelled) setMap(loadFrom(keyFor(sub)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sub]);

  const setStatus = useCallback(
    (id: string, status: ProgressStatus) => {
      setMap((prev) => {
        const next = { ...prev };
        if (status === 'new') delete next[id];
        else next[id] = status;
        saveTo(keyFor(subRef.current), next);
        if (subRef.current) {
          api.putProgress(next).catch((e) => console.error('[progress] sync failed', e));
        }
        return next;
      });
    },
    [],
  );

  const cycle = useCallback((id: string) => {
    setMap((prev) => {
      const current = prev[id] ?? 'new';
      const nextStatus = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];
      const next = { ...prev };
      if (nextStatus === 'new') delete next[id];
      else next[id] = nextStatus;
      saveTo(keyFor(subRef.current), next);
      if (subRef.current) {
        api.putProgress(next).catch((e) => console.error('[progress] sync failed', e));
      }
      return next;
    });
  }, []);

  const statusOf = useCallback((id: string): ProgressStatus => map[id] ?? 'new', [map]);

  return { map, statusOf, setStatus, cycle };
}
